// client/src/components/ticketing/PromoCodeManager.jsx
//
// CRUD panel for ticket promo codes on a ticketed event item
// (server/routes/ticketingRoutes.js — t_ticket_promo_code). Rendered as a
// tab inside TicketConfig.jsx, sharing its AuctionConfig.css theme.
//
// Growth Phase 3 adds: 'free' codes, codes that unlock an access-code-only
// tier, a per-person limit, event-wide and tenant-wide codes, generating
// many single-use codes at once, and CSV export of the list.

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

const EMPTY_PROMO = {
  code: "",
  discount_type: "percent",
  discount_value: "10",
  max_uses: "",
  valid_until: "",
  scope: "item",
  reveals_tier_id: "",
  per_customer_limit: "",
};
const EMPTY_BULK = { count: "50", prefix: "" };

export default function PromoCodeManager({ tenantId, eventId, itemId, tiers = [] }) {
  const { t } = useTranslation();
  const base = `/api/ticketing/${tenantId}/event/${eventId}/item/${itemId}`;

  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY_PROMO);
  const [mode, setMode] = useState("single"); // single | bulk
  const [bulk, setBulk] = useState(EMPTY_BULK);
  const [generated, setGenerated] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const loadCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${base}/promo-codes`);
      if (data.success) setCodes(data.promoCodes);
    } catch (err) {
      console.error("Error loading promo codes:", err);
    }
  }, [base]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  // Shared by one-code and bulk creation.
  const settings = () => ({
    discount_type: form.discount_type,
    discount_value: form.discount_type === "free" ? 0 : Number(form.discount_value),
    max_uses: form.max_uses === "" ? null : Number(form.max_uses),
    valid_until: form.valid_until || null,
    scope: form.scope,
    reveals_tier_id: form.reveals_tier_id === "" ? null : Number(form.reveals_tier_id),
    per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
  });

  const submitCode = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: "", type: "" });
    setGenerated([]);

    try {
      if (mode === "bulk") {
        const s = settings();
        const { data } = await axios.post(`${base}/promo-codes/bulk`, {
          ...s,
          // Bulk codes are single-use unless a limit is set.
          max_uses: s.max_uses ?? 1,
          count: Number(bulk.count),
          prefix: bulk.prefix.trim() || null,
        });
        if (data.success) {
          setMessage({ text: data.message, type: "success" });
          setGenerated(data.codes || []);
          setBulk(EMPTY_BULK);
          loadCodes();
        }
      } else {
        const { data } = await axios.post(`${base}/promo-codes`, { code: form.code.trim(), ...settings() });
        if (data.success) {
          setMessage({ text: data.message, type: "success" });
          setForm(EMPTY_PROMO);
          loadCodes();
        }
      }
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to create promo code", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteCode = async (codeId) => {
    if (!window.confirm("Delete this promo code?")) return;
    try {
      await axios.delete(`${base}/promo-codes/${codeId}`);
      loadCodes();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || "Failed to delete promo code", type: "error" });
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await axios.get(`${base}/promo-codes/export.csv`, { responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `promo-codes-event-${eventId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setMessage({ text: t("ticketing.promo.exportError", "Could not export the codes."), type: "error" });
    }
  };

  const scopeLabel = (c) => {
    if (c.eventid == null) return t("ticketing.promo.scopeTenant", "All events");
    if (c.eventitemid == null) return t("ticketing.promo.scopeEvent", "Whole event");
    return null;
  };

  const discountLabel = (c) => {
    if (c.discount_type === "free") return t("ticketing.promo.freeLabel", "Free");
    return c.discount_type === "percent"
      ? `${Number(c.discount_value)}% off`
      : `${Number(c.discount_value).toFixed(2)} off`;
  };

  return (
    <div>
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <div className="ticket-mode-switch" role="group" aria-label={t("ticketing.promo.mode", "Single code or a batch")}>
        <button type="button" className={`ticket-item-btn${mode === "single" ? " active" : ""}`} aria-pressed={mode === "single"} onClick={() => setMode("single")}>
          {t("ticketing.promo.modeSingle", "One code")}
        </button>
        <button type="button" className={`ticket-item-btn${mode === "bulk" ? " active" : ""}`} aria-pressed={mode === "bulk"} onClick={() => setMode("bulk")}>
          {t("ticketing.promo.modeBulk", "Generate many")}
        </button>
      </div>

      <form onSubmit={submitCode} className="auction-config-form" style={{ padding: 16, borderRadius: 12, marginBottom: 16 }}>
        <div className="form-row">
          {mode === "single" ? (
            <div className="form-group">
              <label htmlFor="promo_code">Code <span className="required">*</span></label>
              <input
                id="promo_code"
                className="uppercase"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="EARLYBIRD10"
                required
              />
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="promo_bulk_count">{t("ticketing.promo.bulkCount", "How many")} <span className="required">*</span></label>
                <input
                  id="promo_bulk_count"
                  type="number" min="1" max="500"
                  value={bulk.count}
                  onChange={(e) => setBulk((b) => ({ ...b, count: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="promo_bulk_prefix">{t("ticketing.promo.bulkPrefix", "Prefix (optional)")}</label>
                <input
                  id="promo_bulk_prefix"
                  className="uppercase"
                  maxLength={20}
                  value={bulk.prefix}
                  onChange={(e) => setBulk((b) => ({ ...b, prefix: e.target.value.replace(/[^a-z0-9]/gi, "") }))}
                  placeholder="GALA"
                />
              </div>
            </>
          )}
          <div className="form-group">
            <label htmlFor="promo_type">Discount Type</label>
            <select
              id="promo_type"
              value={form.discount_type}
              onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
            >
              <option value="percent">Percent off</option>
              <option value="flat">Flat amount off</option>
              <option value="free">{t("ticketing.promo.free", "Free ticket (100% off)")}</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          {form.discount_type !== "free" && (
            <div className="form-group">
              <label htmlFor="promo_value">
                {form.discount_type === "percent" ? "Percent (0-100)" : "Amount off"} <span className="required">*</span>
              </label>
              <input
                id="promo_value"
                type="number"
                min="0"
                step="0.01"
                value={form.discount_value}
                onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="promo_max_uses">Max Uses</label>
            <input
              id="promo_max_uses"
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
              placeholder={mode === "bulk" ? t("ticketing.promo.bulkMaxUses", "Blank = once each") : "Blank = unlimited"}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="promo_per_customer">{t("ticketing.promo.perCustomer", "Uses per person")}</label>
            <input
              id="promo_per_customer"
              type="number"
              min="1"
              value={form.per_customer_limit}
              onChange={(e) => setForm((f) => ({ ...f, per_customer_limit: e.target.value }))}
              placeholder={t("ticketing.promo.perCustomerPlaceholder", "Blank = no limit")}
            />
          </div>
          <div className="form-group">
            <label htmlFor="promo_valid_until">Valid Until</label>
            <input
              id="promo_valid_until"
              type="datetime-local"
              value={form.valid_until}
              onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="promo_scope">{t("ticketing.promo.scope", "Works on")}</label>
            <select
              id="promo_scope"
              value={form.scope}
              onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
            >
              <option value="item">{t("ticketing.promo.scopeItemOption", "This ticketed item")}</option>
              <option value="event">{t("ticketing.promo.scopeEventOption", "Every item in this event")}</option>
              <option value="tenant">{t("ticketing.promo.scopeTenantOption", "All your events (managers only)")}</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="promo_reveals">{t("ticketing.promo.reveals", "Unlocks a hidden ticket")}</label>
            <select
              id="promo_reveals"
              value={form.reveals_tier_id}
              onChange={(e) => setForm((f) => ({ ...f, reveals_tier_id: e.target.value }))}
            >
              <option value="">{t("ticketing.promo.revealsNone", "No")}</option>
              {tiers.filter((tier) => tier.visibility === "code_only").map((tier) => (
                <option key={tier.id} value={tier.id}>{tier.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-actions" style={{ marginTop: 12, paddingTop: 12 }}>
          <button type="submit" disabled={saving} className="save-button">
            {saving
              ? "Saving…"
              : mode === "bulk" ? t("ticketing.promo.generate", "Generate codes") : "Add Promo Code"}
          </button>
        </div>
      </form>

      {generated.length > 0 && (
        <div className="ticket-subpanel" style={{ marginBottom: 16 }}>
          <div className="ticket-list-item-title">
            {t("ticketing.promo.generatedTitle", "{{count}} new codes", { count: generated.length })}
          </div>
          <div className="ticket-code-list" data-testid="generated-codes">{generated.join(", ")}</div>
        </div>
      )}

      <div className="ticket-toolbar">
        <button type="button" className="ticket-item-btn" onClick={exportCsv} disabled={codes.length === 0}>
          {t("ticketing.promo.exportCsv", "Export CSV")}
        </button>
      </div>

      {codes.length === 0 ? (
        <div className="ticket-empty-state">No promo codes yet.</div>
      ) : (
        <ul className="ticket-list">
          {codes.map((code) => (
            <li key={code.id} className="ticket-list-item">
              <div>
                <div className="ticket-list-item-title">
                  {code.code}
                  {scopeLabel(code) && <span className="ticket-badge">{scopeLabel(code)}</span>}
                </div>
                <div className="ticket-list-item-meta">
                  {discountLabel(code)}{" "}
                  · {code.uses_count}/{code.max_uses ?? "∞"} used
                </div>
                {(code.reveals_tier_name || code.per_customer_limit) && (
                  <div className="ticket-list-item-meta">
                    {code.reveals_tier_name && t("ticketing.promo.unlocks", "Unlocks {{tier}}", { tier: code.reveals_tier_name })}
                    {code.reveals_tier_name && code.per_customer_limit ? " · " : ""}
                    {code.per_customer_limit && t("ticketing.promo.perCustomerShort", "{{count}} per person", { count: code.per_customer_limit })}
                  </div>
                )}
              </div>
              <button onClick={() => deleteCode(code.id)} className="ticket-item-btn danger">
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
